import type { RouteObject } from 'react-router';
import { Outlet, useLocation } from 'react-router';

import { Navigate, catchAll } from '@sorare/routing';
import {
  ANY_SPORT_CARDS_SLUG,
  FOOTBALL,
  MLB,
  NBA,
} from '@sorare/core/src/constants/__generated__/routes';
import { loader } from '@sorare/core/src/contexts/config/Provider';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { lazy } from '@sorare/core/src/lib/retry';
import { relative } from '@sorare/core/src/lib/routing';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { RouteErrorBoundary } from '@sorare/core/src/routing/RouteErrorBoundary';
import { AppLayout } from '@sorare/gameplay/src/components/navigation/AppLayout';
import { routeObjects as baseballPages } from '@sorare/baseball/src/pages';
import { routeObjects as blogPages } from '@sorare/blog/src/pages';
import { routeObjects as footballPages } from '@sorare/football/src/pages';
import { routeObjects as helpCenterPages } from '@sorare/help/src/pages';
import { routeObjects as nbaPages } from '@sorare/nba/src/pages';
import { routeObjects as sharedPages } from '@sorare/shared-pages/src/pages';

import { InnerAppLayout, OuterAppLayout } from './AppLayout.tsx';

const AnyCardPageRoute = lazy(
  async () => import('@sorare/gameplay/src/components/AnyCardPage'),
  'AnyCardPageRoute'
);

const RedirectToSport = () => {
  const { sport } = useSportContext();
  const location = useLocation();

  return (
    <Navigate
      to={generateSportPath(location.pathname, {
        sport,
      })}
      replace
      keepSearchParams
    />
  );
};

export const routeObjects: RouteObject[] = [
  {
    loader,
    shouldRevalidate: loader.shouldRevalidate,
    element: <OuterAppLayout />,
    ErrorBoundary: RouteErrorBoundary,
    children: [
      {
        element: <InnerAppLayout />,
        children: [
          ...sharedPages,
          ...helpCenterPages,
          {
            element: (
              <AppLayout>
                <Outlet />
              </AppLayout>
            ),
            children: blogPages,
          },
          {
            path: relative('/', catchAll(FOOTBALL)),
            children: footballPages,
          },
          {
            path: MLB,
            children: baseballPages,
          },
          {
            path: NBA,
            children: nbaPages,
          },
          {
            path: ANY_SPORT_CARDS_SLUG,
            element: <AnyCardPageRoute />,
          },
          {
            path: '/*',
            element: <RedirectToSport />,
          },
        ],
      },
    ],
  },
];
